import * as Cesium from "cesium";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";
import { getUsernameAndUserIdFormat } from "../Utility";

// read SVG and build normalized local shape
export async function loadIconShapeFromSvg(url) {
  // fetch svg text
  const res = await fetch(url);
  const svgText = await res.text();

  // parse svg
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, "image/svg+xml");
  const svgEl = doc.documentElement;

  const rawPoints = [];

  // matrix helpers
  function identityMatrix() {
    return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  }

  function multiplyMatrix(m1, m2) {
    // m = m1 * m2
    return {
      a: m1.a * m2.a + m1.c * m2.b,
      b: m1.b * m2.a + m1.d * m2.b,
      c: m1.a * m2.c + m1.c * m2.d,
      d: m1.b * m2.c + m1.d * m2.d,
      e: m1.a * m2.e + m1.c * m2.f + m1.e,
      f: m1.b * m2.e + m1.d * m2.f + m1.f,
    };
  }

  function matrixFromTransformItem(type, values) {
    switch (type) {
      case "translate": {
        const tx = values[0] || 0;
        const ty = values[1] || 0;
        return { a: 1, b: 0, c: 0, d: 1, e: tx, f: ty };
      }
      case "scale": {
        const sx = values[0] != null ? values[0] : 1;
        const sy = values[1] != null ? values[1] : sx;
        return { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
      }
      case "rotate": {
        const angle = (values[0] || 0) * (Math.PI / 180);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        if (values.length > 1) {
          const cx = values[1] || 0;
          const cy = values[2] || 0;
          // T(cx,cy) * R(angle) * T(-cx,-cy)
          const t1 = { a: 1, b: 0, c: 0, d: 1, e: cx, f: cy };
          const r = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
          const t2 = { a: 1, b: 0, c: 0, d: 1, e: -cx, f: -cy };
          return multiplyMatrix(multiplyMatrix(t1, r), t2);
        }
        return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
      }
      case "skewX": {
        const angle = (values[0] || 0) * (Math.PI / 180);
        return { a: 1, b: 0, c: Math.tan(angle), d: 1, e: 0, f: 0 };
      }
      case "skewY": {
        const angle = (values[0] || 0) * (Math.PI / 180);
        return { a: 1, b: Math.tan(angle), c: 0, d: 1, e: 0, f: 0 };
      }
      case "matrix": {
        const [a, b, c, d, e, f] = values;
        return {
          a: a != null ? a : 1,
          b: b != null ? b : 0,
          c: c != null ? c : 0,
          d: d != null ? d : 1,
          e: e != null ? e : 0,
          f: f != null ? f : 0,
        };
      }
      default:
        return identityMatrix();
    }
  }

  function parseTransformString(str) {
    if (!str || typeof str !== "string") return identityMatrix();

    const regex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
    let match;
    let m = identityMatrix();

    while ((match = regex.exec(str)) !== null) {
      const type = match[1];
      const raw = match[2].trim();
      const values = raw
        .split(/[,\s]+/) // split by comma or whitespace
        .filter((v) => v.length)
        .map((v) => Number(v));
      const itemM = matrixFromTransformItem(type, values);
      m = multiplyMatrix(m, itemM); // compose with previous
    }

    return m;
  }

  function getNodeTransformMatrix(el) {
    let m = identityMatrix();
    let current = el;

    // walk up until root svg element
    while (current && current !== svgEl.parentNode) {
      if (current.getAttribute) {
        const tf = current.getAttribute("transform");
        if (tf) {
          const itemM = parseTransformString(tf);
          m = multiplyMatrix(itemM, m); // parent * child
        }
      }
      if (current === svgEl) break;
      current = current.parentNode;
    }

    return m;
  }

  function applyMatrixToPoint(m, x, y) {
    return {
      x: m.a * x + m.c * y + m.e,
      y: m.b * x + m.d * y + m.f,
    };
  }

  function pushPoint(el, x, y) {
    const m = getNodeTransformMatrix(el);
    const p = applyMatrixToPoint(m, x, y);
    rawPoints.push(p);
  }

  // polygons and polylines
  svgEl.querySelectorAll("polygon, polyline").forEach((poly) => {
    const pointsAttr = poly.getAttribute("points");
    if (!pointsAttr) return;

    const nums = pointsAttr
      .trim()
      .split(/[,\s]+/)
      .map((v) => Number(v));

    for (let i = 0; i < nums.length; i += 2) {
      const x = nums[i];
      const y = nums[i + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      pushPoint(poly, x, y);
    }
  });

  // rect
  svgEl.querySelectorAll("rect").forEach((r) => {
    const x = Number(r.getAttribute("x") || 0);
    const y = Number(r.getAttribute("y") || 0);
    const w = Number(r.getAttribute("width") || 0);
    const h = Number(r.getAttribute("height") || 0);
    if (!w || !h) return;

    pushPoint(r, x, y);
    pushPoint(r, x + w, y);
    pushPoint(r, x + w, y + h);
    pushPoint(r, x, y + h);
  });

  // circle
  svgEl.querySelectorAll("circle").forEach((c) => {
    const cx = Number(c.getAttribute("cx") || 0);
    const cy = Number(c.getAttribute("cy") || 0);
    const r = Number(c.getAttribute("r") || 0);
    if (!r) return;

    const samples = 144; // a bit more for smoothness
    for (let i = 0; i < samples; i++) {
      const t = (i / samples) * 2 * Math.PI;
      const x = cx + r * Math.cos(t);
      const y = cy + r * Math.sin(t);
      pushPoint(c, x, y);
    }
  });

  // ellipse
  svgEl.querySelectorAll("ellipse").forEach((e) => {
    const cx = Number(e.getAttribute("cx") || 0);
    const cy = Number(e.getAttribute("cy") || 0);
    const rx = Number(e.getAttribute("rx") || 0);
    const ry = Number(e.getAttribute("ry") || 0);
    if (!rx || !ry) return;

    const samples = 144; // a bit more for smoothness
    for (let i = 0; i < samples; i++) {
      const t = (i / samples) * 2 * Math.PI;
      const x = cx + rx * Math.cos(t);
      const y = cy + ry * Math.sin(t);
      pushPoint(e, x, y);
    }
  });

  // line
  svgEl.querySelectorAll("line").forEach((ln) => {
    const x1 = Number(ln.getAttribute("x1") || 0);
    const y1 = Number(ln.getAttribute("y1") || 0);
    const x2 = Number(ln.getAttribute("x2") || 0);
    const y2 = Number(ln.getAttribute("y2") || 0);

    const segments = 2; // two points are enough for a line
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = x1 + (x2 - x1) * t;
      const y = y1 + (y2 - y1) * t;
      pushPoint(ln, x, y);
    }
  });

  // path
  svgEl.querySelectorAll("path").forEach((pathEl) => {
    try {
      const len = pathEl.getTotalLength();
      const samples = Math.max(96, Math.floor(len / 2)); // denser sampling
      for (let i = 0; i <= samples; i++) {
        const p = pathEl.getPointAtLength((i / samples) * len);
        pushPoint(pathEl, p.x, p.y);
      }
    } catch (e) {
      // ignore bad path
      console.warn("Cannot sample path", e);
    }
  });

  if (!rawPoints.length) {
    console.error("SVG has no supported shapes:", url);
    return null;
  }

  // compute bounds (raw)
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  rawPoints.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  });

  // override bounds by viewBox if present
  const vbAttr = svgEl.getAttribute("viewBox");
  if (vbAttr) {
    const parts = vbAttr
      .trim()
      .split(/[,\s]+/)
      .map((v) => Number(v));
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      const [vbX, vbY, vbW, vbH] = parts;
      minX = vbX;
      minY = vbY;
      maxX = vbX + vbW;
      maxY = vbY + vbH;
    }
  }

  const width = maxX - minX || 1;
  const height = maxY - minY || 1;
  const centerX = minX + width / 2;

  // normalize: y in [0,1] bottom-up, x scaled by height to keep aspect
  const shapePoints = rawPoints.map((p) => {
    const nx = (p.x - centerX) / height;
    const nySvg = (p.y - minY) / height;
    const ny = 1 - nySvg;
    return { x: nx, y: ny };
  });

  return shapePoints;
}

// start picking two points on map
export function startRoadMarkingFlow(viewer, shapePoints, onFinish) {
  // simple guard
  if (!viewer) {
    console.error("viewer is not ready");
    return;
  }

  if (!shapePoints || !shapePoints.length) {
    console.error("icon shapePoints are empty");
    return;
  }

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  let escHandler = null;

  const cleanupHelpers = () => {
    if (startPointEntity) viewer.entities.remove(startPointEntity);
    if (endPointEntity) viewer.entities.remove(endPointEntity);
    startPointEntity = null;
    endPointEntity = null;
  };

  escHandler = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    try {
      handler.destroy();
    } catch (err) {}
    cleanupHelpers();
    if (typeof onFinish === "function") onFinish({ feature: null, canceled: true });
  };

  window.addEventListener("keydown", escHandler, { capture: true });

  let clickCount = 0;
  let startPoint = null;
  let startPointEntity = null;
  let endPointEntity = null;

  // listen for left click
  handler.setInputAction((movement) => {
    // get 3D position on globe
    const cartesian = viewer.scene.pickPosition(movement.position);
    if (!cartesian) return;

    // convert to lat/lon
    const carto = Cesium.Cartographic.fromCartesian(cartesian);
    const lat = Cesium.Math.toDegrees(carto.latitude);
    const lon = Cesium.Math.toDegrees(carto.longitude);

    if (clickCount === 0) {
      // first point = bottom of icon
      startPoint = { lat, lon };
      clickCount = 1;

      // show first point on map
      startPointEntity = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        point: {
          pixelSize: 10,
          color: Cesium.Color.YELLOW,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
        },
      });

      return;
    }

    if (clickCount === 1) {
      // second point = top of icon
      const endPoint = { lat, lon };
      clickCount = 2;

      // show second point on map
      endPointEntity = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        point: {
          pixelSize: 10,
          color: Cesium.Color.RED,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
        },
      });

      // stop listening
      handler.destroy();

      // create polygon coords from icon shape
      const result = createRoadMarkingFromIcon(viewer, startPoint, endPoint, shapePoints, startPointEntity, endPointEntity);

      if (typeof onFinish === "function") {
        onFinish(result);
      }
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  const originalDestroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    if (escHandler) {
      window.removeEventListener("keydown", escHandler, { capture: true });
      escHandler = null;
    }
    cleanupHelpers();
    originalDestroy();
  };

  return handler;
}

// create polygon coords based on icon local shape and two points on map
function createRoadMarkingFromIcon(viewer, startPoint, endPoint, shapePoints, startPointEntity, endPointEntity) {
  // start & end in world coords
  const startCartesian = Cesium.Cartesian3.fromDegrees(startPoint.lon, startPoint.lat);
  const endCartesian = Cesium.Cartesian3.fromDegrees(endPoint.lon, endPoint.lat);

  // direction vector along icon (bottom -> top)
  const dir = new Cesium.Cartesian3();
  Cesium.Cartesian3.subtract(endCartesian, startCartesian, dir);
  const length = Cesium.Cartesian3.magnitude(dir);
  Cesium.Cartesian3.normalize(dir, dir);

  // up and right on surface
  const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(startCartesian, new Cesium.Cartesian3());

  const right = new Cesium.Cartesian3();
  Cesium.Cartesian3.cross(dir, up, right);
  Cesium.Cartesian3.normalize(right, right);

  // full width of icon relative to length
  const widthScale = 1; // can be tuned
  const fullWidthMeters = length * widthScale;

  const worldPoints = [];

  // transform each local point (x,y) to world Cartesian
  shapePoints.forEach((p) => {
    // x in [-0.5,0.5] => sideways
    // y in [0,1]      => along length from start to end
    const sideOffsetMeters = p.x * fullWidthMeters;
    const alongOffsetMeters = p.y * length;

    const alongVec = new Cesium.Cartesian3();
    const sideVec = new Cesium.Cartesian3();
    Cesium.Cartesian3.multiplyByScalar(dir, alongOffsetMeters, alongVec);
    Cesium.Cartesian3.multiplyByScalar(right, sideOffsetMeters, sideVec);

    const worldPoint = new Cesium.Cartesian3();
    Cesium.Cartesian3.add(startCartesian, alongVec, worldPoint);
    Cesium.Cartesian3.add(worldPoint, sideVec, worldPoint);

    worldPoints.push(worldPoint);
  });

  // convert world points to lat/lon
  const cornersCarto = worldPoints.map((p) => {
    const c = Cesium.Cartographic.fromCartesian(p);
    return {
      lat: Cesium.Math.toDegrees(c.latitude),
      lon: Cesium.Math.toDegrees(c.longitude),
    };
  });

  // remove helper points
  if (startPointEntity) viewer.entities.remove(startPointEntity);
  if (endPointEntity) viewer.entities.remove(endPointEntity);

  // build GeoJSON polygon from the same points
  const coordinates = cornersCarto.map((c) => [c.lon, c.lat]);
  coordinates.push([cornersCarto[0].lon, cornersCarto[0].lat]);

  const feature = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [coordinates],
    },
  };

  return {
    startPoint,
    endPoint,
    points: cornersCarto,
    feature,
  };
}

export function startRoadMarkingMoveMode(viewer, dataSource, originalCameraControls, onEdited, userData) {
  // guard
  if (!viewer || !dataSource) {
    console.error("viewer or dataSource is not ready");
    return null;
  }

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  let escHandler = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    try {
      handler.destroy();
    } catch (err) {}
  };

  window.addEventListener("keydown", escHandler, { capture: true });

  const ellipsoid = viewer.scene.globe.ellipsoid;
  const translationScratch = new Cesium.Cartesian3();
  const modifier = getUsernameAndUserIdFormat(userData);

  // move state
  let movingEntity = null;
  let dragStartCartesian = null;
  let basePositions = null;
  let currentPositions = null;

  // camera lock
  let cameraLocked = false;

  // click vs drag detection
  let isLeftDown = false;
  let suppressNextClick = false;

  // transform state
  let transformActive = false;
  let transformTarget = null;
  let transformPlane = null;
  let transformCenter2D = null;
  let basePositions2D = null;
  let bboxBaseRect = null;
  let bboxBaseCorners2D = null;
  let bboxEntity = null;
  let rotationBillboard = null;
  let scaleHandles = [];
  let handleBaseDirections2D = [];
  let baseHalfWidth = 0.0;
  let baseHalfHeight = 0.0;

  // live transformed positions (used by CallbackProperty for smooth updates)
  let transformedPolygonPositions = null;
  let transformedBboxPositions = null;

  let transformRotation = 0.0;
  let scaleX = 1.0;
  let scaleY = 1.0;

  let transformDragging = false;
  let activeHandleType = null; // 'rotate' | 'scale'
  let activeHandleIndex = -1; // 0..3 for scale

  // pick ground cartesian
  const pickGround = (screenPos) => {
    if (!screenPos) return null;

    if (viewer.scene.pickPositionSupported) {
      const p = viewer.scene.pickPosition(screenPos);
      if (Cesium.defined(p)) return p;
    }

    const c = viewer.camera.pickEllipsoid(screenPos, ellipsoid);
    if (Cesium.defined(c)) return c;

    const ray = viewer.camera.getPickRay(screenPos);
    if (!Cesium.defined(ray)) return null;
    return viewer.scene.globe.pick(ray, viewer.scene);
  };

  // project screen to local plane 2D
  function screenToPlane2D(screenPos) {
    if (!transformPlane) return null;
    const cart = pickGround(screenPos);
    if (!cart) return null;
    const projected = transformPlane.projectPointOntoPlane(cart);
    if (!projected) return null;
    return projected;
  }

  // rotate 2D vector
  function rotate2D(vec2, angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Cesium.Cartesian2(vec2.x * cos - vec2.y * sin, vec2.x * sin + vec2.y * cos);
  }

  // create bbox + handles for one polygon
  function createTransformOverlay(entity) {
    destroyTransformOverlay();

    const time = viewer.clock.currentTime;
    const hierarchy = entity.polygon.hierarchy.getValue(time);
    if (!hierarchy || !hierarchy.positions || hierarchy.positions.length === 0) return;

    const positions = hierarchy.positions;

    transformPlane = Cesium.EllipsoidTangentPlane.fromPoints(positions, ellipsoid);
    const projected2D = transformPlane.projectPointsOntoPlane(positions);
    if (!projected2D || projected2D.length === 0) {
      transformPlane = null;
      return;
    }

    basePositions2D = projected2D;

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let i = 0; i < projected2D.length; i++) {
      const p = projected2D[i];
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }

    baseHalfWidth = (maxX - minX) * 0.5;
    baseHalfHeight = (maxY - minY) * 0.5;

    const centerX = (minX + maxX) * 0.5;
    const centerY = (minY + maxY) * 0.5;
    transformCenter2D = new Cesium.Cartesian2(centerX, centerY);

    bboxBaseRect = { minX, maxX, minY, maxY };

    const c0 = new Cesium.Cartesian2(minX, minY);
    const c1 = new Cesium.Cartesian2(maxX, minY);
    const c2 = new Cesium.Cartesian2(maxX, maxY);
    const c3 = new Cesium.Cartesian2(minX, maxY);
    bboxBaseCorners2D = [c0, c1, c2, c3];

    const bboxCorners3D = transformPlane.projectPointsOntoEllipsoid(bboxBaseCorners2D);

    // init live positions arrays
    transformedPolygonPositions = positions.map((p) => Cesium.Cartesian3.clone(p));
    transformedBboxPositions = bboxCorners3D.map((p) => Cesium.Cartesian3.clone(p));

    // bbox polygon with CallbackProperty (for smooth live updates)
    bboxEntity = dataSource.entities.add({
      polygon: {
        hierarchy: new Cesium.CallbackProperty(() => {
          return new Cesium.PolygonHierarchy(transformedBboxPositions);
        }, false),
        material: Cesium.Color.YELLOW.withAlpha(0.15),
        outline: true,
        outlineColor: Cesium.Color.YELLOW,
        // heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
      },
    });
    bboxEntity._roadMarkingHelper = true;

    // rotation handle (top-right corner)
    const rotationPos = transformedBboxPositions[2];
    rotationBillboard = dataSource.entities.add({
      position: rotationPos,
      billboard: {
        image: "/icons/rotate.svg", // your SVG file
        width: 28,
        height: 28,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
    rotationBillboard._roadMarkingHelper = true;
    rotationBillboard._roadMarkingRotationHandle = true;

    // scale handles (midpoints of edges), also static billboards but positions updated in code
    scaleHandles = [];
    handleBaseDirections2D = [];

    const corners2D = bboxBaseCorners2D;

    for (let i = 0; i < 4; i++) {
      const a = corners2D[i];
      const b = corners2D[(i + 1) % 4];
      const mid = new Cesium.Cartesian2((a.x + b.x) * 0.5, (a.y + b.y) * 0.5);

      const dir = new Cesium.Cartesian2(mid.x - centerX, mid.y - centerY);
      const len = Math.hypot(dir.x, dir.y) || 1.0;
      dir.x /= len;
      dir.y /= len;
      handleBaseDirections2D.push(dir);

      const mid3D = transformPlane.projectPointsOntoEllipsoid([mid])[0];

      const handle = dataSource.entities.add({
        position: mid3D,
        billboard: {
          image: "/icons/square.svg",
          width: 10,
          height: 10,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      handle._roadMarkingHelper = true;
      handle._roadMarkingScaleHandle = true;
      handle._roadMarkingScaleIndex = i; // 0: bottom,1:right,2:top,3:left

      scaleHandles.push(handle);
    }

    transformRotation = 0.0;
    scaleX = 1.0;
    scaleY = 1.0;

    transformActive = true;
    transformTarget = entity;

    // polygon also becomes live via CallbackProperty
    transformTarget.polygon.hierarchy = new Cesium.CallbackProperty(() => {
      return new Cesium.PolygonHierarchy(transformedPolygonPositions);
    }, false);

    updateTransformGeometry();
    viewer.scene.requestRender();
  }

  // remove bbox + handles, bake final shape into ConstantProperty
  function destroyTransformOverlay() {
    // bake final polygon if we have live positions
    if (transformTarget && transformedPolygonPositions) {
      const baked = transformedPolygonPositions.map((p) => Cesium.Cartesian3.clone(p));
      transformTarget.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(baked));
    }

    if (bboxEntity && dataSource.entities.contains(bboxEntity)) {
      dataSource.entities.remove(bboxEntity);
    }
    bboxEntity = null;

    if (rotationBillboard && dataSource.entities.contains(rotationBillboard)) {
      dataSource.entities.remove(rotationBillboard);
    }
    rotationBillboard = null;

    if (scaleHandles && scaleHandles.length) {
      for (let i = 0; i < scaleHandles.length; i++) {
        const h = scaleHandles[i];
        if (h && dataSource.entities.contains(h)) {
          dataSource.entities.remove(h);
        }
      }
    }
    scaleHandles = [];
    handleBaseDirections2D = [];

    transformActive = false;
    transformTarget = null;
    transformPlane = null;
    transformCenter2D = null;
    basePositions2D = null;
    bboxBaseRect = null;
    bboxBaseCorners2D = null;
    transformRotation = 0.0;
    scaleX = 1.0;
    scaleY = 1.0;
    transformedPolygonPositions = null;
    transformedBboxPositions = null;

    transformDragging = false;
    activeHandleType = null;
    activeHandleIndex = -1;
  }

  // update polygon + bbox based on current rotation/scale (live)
  function updateTransformGeometry() {
    if (!transformActive || !transformPlane || !transformCenter2D || !basePositions2D || !transformTarget || !transformedPolygonPositions || !transformedBboxPositions) {
      return;
    }

    const cx = transformCenter2D.x;
    const cy = transformCenter2D.y;
    const cos = Math.cos(transformRotation);
    const sin = Math.sin(transformRotation);

    // polygon vertices
    const transformed2D = new Array(basePositions2D.length);
    for (let i = 0; i < basePositions2D.length; i++) {
      const p = basePositions2D[i];
      const dx = (p.x - cx) * scaleX;
      const dy = (p.y - cy) * scaleY;

      const rx = dx * cos - dy * sin;
      const ry = dx * sin + dy * cos;

      transformed2D[i] = new Cesium.Cartesian2(cx + rx, cy + ry);
    }

    const transformed3D = transformPlane.projectPointsOntoEllipsoid(transformed2D);

    // mutate live polygon positions
    transformedPolygonPositions.length = transformed3D.length;
    for (let i = 0; i < transformed3D.length; i++) {
      transformedPolygonPositions[i] = Cesium.Cartesian3.clone(transformed3D[i], transformedPolygonPositions[i]);
    }

    // bbox + handles
    if (bboxBaseCorners2D && bboxEntity) {
      const bboxTrans2D = new Array(4);
      for (let i = 0; i < 4; i++) {
        const p = bboxBaseCorners2D[i];
        const dx = (p.x - cx) * scaleX;
        const dy = (p.y - cy) * scaleY;

        const rx = dx * cos - dy * sin;
        const ry = dx * sin + dy * cos;

        bboxTrans2D[i] = new Cesium.Cartesian2(cx + rx, cy + ry);
      }

      const bboxTrans3D = transformPlane.projectPointsOntoEllipsoid(bboxTrans2D);

      // mutate live bbox positions
      transformedBboxPositions.length = bboxTrans3D.length;
      for (let i = 0; i < bboxTrans3D.length; i++) {
        transformedBboxPositions[i] = Cesium.Cartesian3.clone(bboxTrans3D[i], transformedBboxPositions[i]);
      }

      // rotation handle follows corner 2
      if (rotationBillboard) {
        rotationBillboard.position = transformedBboxPositions[2];
      }

      // scale handles to edge midpoints
      if (scaleHandles && scaleHandles.length === 4) {
        for (let i = 0; i < 4; i++) {
          const a = bboxTrans2D[i];
          const b = bboxTrans2D[(i + 1) % 4];
          const mid2D = new Cesium.Cartesian2((a.x + b.x) * 0.5, (a.y + b.y) * 0.5);
          const mid3D = transformPlane.projectPointsOntoEllipsoid([mid2D])[0];
          scaleHandles[i].position = mid3D;
        }
      }
    }
  }

  // LEFT_DOWN
  handler.setInputAction((click) => {
    isLeftDown = true;
    suppressNextClick = false;

    const picked = viewer.scene.pick(click.position);
    if (!Cesium.defined(picked) || !picked.id) return;

    const entity = picked.id;

    // rotation handle
    if (entity._roadMarkingRotationHandle && transformActive && transformTarget) {
      const mouse2D = screenToPlane2D(click.position);
      if (!mouse2D || !transformCenter2D) return;

      const dx = mouse2D.x - transformCenter2D.x;
      const dy = mouse2D.y - transformCenter2D.y;
      const angle = Math.atan2(dy, dx);

      transformDragging = true;
      activeHandleType = "rotate";
      activeHandleIndex = -1;

      // store on handle entity
      entity._startMouseAngle = angle;
      entity._startRotation = transformRotation;

      if (originalCameraControls && !cameraLocked) {
        disableCameraControls(viewer, originalCameraControls);
        cameraLocked = true;
      }
      return;
    }

    // scale handle
    if (entity._roadMarkingScaleHandle && transformActive && transformTarget) {
      const idx = entity._roadMarkingScaleIndex;
      if (idx === undefined) return;

      transformDragging = true;
      activeHandleType = "scale";
      activeHandleIndex = idx;

      if (originalCameraControls && !cameraLocked) {
        disableCameraControls(viewer, originalCameraControls);
        cameraLocked = true;
      }
      return;
    }

    // ignore helpers
    if (entity._roadMarkingHelper) return;

    // do not start move when transform active
    if (transformActive) return;

    if (!entity.polygon) return;
    if (!dataSource.entities.contains(entity)) return;

    const cartesian = pickGround(click.position);
    if (!cartesian) return;

    if (originalCameraControls && !cameraLocked) {
      disableCameraControls(viewer, originalCameraControls);
      cameraLocked = true;
    }

    const time = viewer.clock.currentTime;
    const hierarchy = entity.polygon.hierarchy.getValue(time);
    if (!hierarchy || !hierarchy.positions || hierarchy.positions.length === 0) return;

    basePositions = hierarchy.positions.map((p) => Cesium.Cartesian3.clone(p));
    currentPositions = basePositions.map((p) => Cesium.Cartesian3.clone(p));

    movingEntity = entity;
    dragStartCartesian = Cesium.Cartesian3.clone(cartesian);

    // live move via CallbackProperty
    movingEntity.polygon.hierarchy = new Cesium.CallbackProperty(() => {
      return new Cesium.PolygonHierarchy(currentPositions);
    }, false);

    viewer.scene.requestRender();
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  // MOUSE_MOVE
  handler.setInputAction((movement) => {
    const screenPos = movement.endPosition || movement.startPosition;
    if (!screenPos) return;

    // detect drag for click suppression
    if (isLeftDown && movement.startPosition && movement.endPosition) {
      const dx = movement.endPosition.x - movement.startPosition.x;
      const dy = movement.endPosition.y - movement.startPosition.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        suppressNextClick = true;
      }
    }

    // transform drag (rotate / scale)
    if (transformDragging && transformActive && transformTarget && transformPlane && transformCenter2D) {
      // rotate
      if (activeHandleType === "rotate") {
        const mouse2D = screenToPlane2D(screenPos);
        if (!mouse2D) return;

        const dx = mouse2D.x - transformCenter2D.x;
        const dy = mouse2D.y - transformCenter2D.y;
        const angle = Math.atan2(dy, dx);

        const handleEntity = rotationBillboard;
        if (!handleEntity || handleEntity._startMouseAngle === undefined) return;

        const delta = angle - handleEntity._startMouseAngle;
        transformRotation = handleEntity._startRotation + delta;

        updateTransformGeometry();
        viewer.scene.requestRender();
        return;
      }

      // scale
      if (activeHandleType === "scale" && baseHalfWidth > 0 && baseHalfHeight > 0) {
        const mouse2D = screenToPlane2D(screenPos);
        if (!mouse2D) return;

        const axisBase = handleBaseDirections2D[activeHandleIndex];
        if (!axisBase) return;

        const axisWorld = rotate2D(axisBase, transformRotation);
        const delta = new Cesium.Cartesian2(mouse2D.x - transformCenter2D.x, mouse2D.y - transformCenter2D.y);

        const proj = Cesium.Cartesian2.dot(delta, axisWorld);
        const absProj = Math.abs(proj);

        const isHorizontal = activeHandleIndex === 1 || activeHandleIndex === 3;
        const baseExtent = isHorizontal ? baseHalfWidth : baseHalfHeight;
        if (baseExtent <= 0.0) return;

        const minExtent = baseExtent * 0.1;
        const newHalfExtent = Math.max(minExtent, absProj);
        const newScale = newHalfExtent / baseExtent;

        if (isHorizontal) {
          scaleX = newScale;
        } else {
          scaleY = newScale;
        }

        updateTransformGeometry();
        viewer.scene.requestRender();
        return;
      }

      return;
    }

    // move drag (translate entity)
    if (!movingEntity || !dragStartCartesian || !basePositions || !currentPositions) return;

    const newCartesian = pickGround(screenPos);
    if (!newCartesian) return;

    Cesium.Cartesian3.subtract(newCartesian, dragStartCartesian, translationScratch);

    for (let i = 0; i < basePositions.length; i++) {
      Cesium.Cartesian3.add(basePositions[i], translationScratch, currentPositions[i]);
    }

    viewer.scene.requestRender();
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  // LEFT_UP
  handler.setInputAction(() => {
    isLeftDown = false;

    // finish transform (rotate / scale)
    if (transformDragging) {
      // ensure final frame is applied
      updateTransformGeometry();

      transformDragging = false;
      activeHandleType = null;
      activeHandleIndex = -1;

      if (modifier && transformTarget) {
        const currentProps = transformTarget.properties?.getValue ? transformTarget.properties.getValue(Cesium.JulianDate.now()) || {} : transformTarget.properties || {};
        transformTarget.properties = new Cesium.PropertyBag({
          ...currentProps,
          modifier,
        });
      }

      if (typeof onEdited === "function") {
        onEdited();
      }

      if (cameraLocked && originalCameraControls) {
        restoreCameraControls(viewer, originalCameraControls);
        cameraLocked = false;
      }

      viewer.scene.requestRender();
      return;
    }

    // finish move
    if (movingEntity && currentPositions) {
      const finalPositions = currentPositions.map((p) => Cesium.Cartesian3.clone(p));
      movingEntity.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(finalPositions));
    }

    if (movingEntity && modifier) {
      const currentProps = movingEntity.properties?.getValue ? movingEntity.properties.getValue(Cesium.JulianDate.now()) || {} : movingEntity.properties || {};
      movingEntity.properties = new Cesium.PropertyBag({
        ...currentProps,
        modifier,
      });
    }

    if (movingEntity && typeof onEdited === "function") {
      onEdited();
    }

    movingEntity = null;
    dragStartCartesian = null;
    basePositions = null;
    currentPositions = null;

    if (cameraLocked && originalCameraControls) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }

    viewer.scene.requestRender();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  // LEFT_CLICK: toggle bbox for polygon
  handler.setInputAction((click) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }

    const picked = viewer.scene.pick(click.position);
    if (!Cesium.defined(picked) || !picked.id) {
      if (transformActive) {
        destroyTransformOverlay();
        viewer.scene.requestRender();
      }
      return;
    }

    const entity = picked.id;

    // ignore helpers
    if (entity._roadMarkingHelper) return;

    if (!entity.polygon) return;
    if (!dataSource.entities.contains(entity)) return;

    // toggle bbox
    if (transformActive && transformTarget === entity) {
      destroyTransformOverlay();
    } else {
      createTransformOverlay(entity);
    }

    viewer.scene.requestRender();
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  // destroy
  const originalDestroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    if (escHandler) {
      window.removeEventListener("keydown", escHandler, { capture: true });
      escHandler = null;
    }

    if (cameraLocked && originalCameraControls) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }
    destroyTransformOverlay();
    originalDestroy();
  };

  return handler;
}
